import { c as _c } from "react/compiler-runtime";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { cn } from "cn";
import type * as React from "react";
import { useId } from "react";

// ─── Radio ────────────────────────────────────────────────────────────────────

interface RadioProps extends React.ComponentProps<typeof RadioGroupPrimitive.Item> {}
function Radio(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("group relative flex size-4 shrink-0 cursor-pointer items-center justify-center", "rounded-full border border-input bg-transparent outline-none transition-colors", "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50", "aria-[invalid=true]:border-(--notification-destructive-fg)", "aria-[invalid=true]:focus-visible:ring-destructive/20 dark:aria-[invalid=true]:focus-visible:ring-destructive/40", "disabled:pointer-events-none disabled:opacity-50", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <RadioGroupPrimitive.Indicator className={cn("size-2 rounded-full bg-foreground", "group-aria-[invalid=true]:bg-(--notification-destructive-fg)")} />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t1) {
    t3 = <RadioGroupPrimitive.Item data-slot="radio" className={t1} {...props}>{t2}</RadioGroupPrimitive.Item>;
    $[6] = props;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

// ─── RadioGroup ───────────────────────────────────────────────────────────────

interface RadioGroupProps extends React.ComponentProps<typeof RadioGroupPrimitive.Root> {
  layout?: "inline" | "block";
}
function RadioGroup(t0) {
  const $ = _c(11);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      layout: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const layout = t1 === undefined ? "block" : t1;
  const t2 = layout === "inline" ? "horizontal" : "vertical";
  const t3 = layout === "inline" ? "flex-row flex-wrap gap-3" : "flex-col gap-2";
  let t4;
  if ($[4] !== className || $[5] !== t3) {
    t4 = cn("flex", t3, className);
    $[4] = className;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== props || $[8] !== t2 || $[9] !== t4) {
    t5 = <RadioGroupPrimitive.Root data-slot="radio-group" orientation={t2} className={t4} {...props} />;
    $[7] = props;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
interface RadioGroupItemProps extends React.ComponentProps<typeof RadioGroupPrimitive.Item> {
  label: React.ReactNode;
  error?: boolean;
}
function RadioGroupItem(t0) {
  const $ = _c(17);
  let className;
  let error;
  let externalId;
  let label;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      label,
      error,
      id: externalId,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = error;
    $[3] = externalId;
    $[4] = label;
    $[5] = props;
  } else {
    className = $[1];
    error = $[2];
    externalId = $[3];
    label = $[4];
    props = $[5];
  }
  const generatedId = useId();
  const id = externalId ?? generatedId;
  const t1 = error ? "true" : undefined;
  let t2;
  if ($[6] !== className || $[7] !== id || $[8] !== props || $[9] !== t1) {
    t2 = <Radio id={id} aria-invalid={t1} className={className} {...props} />;
    $[6] = className;
    $[7] = id;
    $[8] = props;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== id || $[12] !== label) {
    t3 = <label htmlFor={id} className="label-md cursor-pointer select-none text-foreground">{label}</label>;
    $[11] = id;
    $[12] = label;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== t2 || $[15] !== t3) {
    t4 = <div className="flex items-center gap-2">{t2}{t3}</div>;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}

// ─── RichRadioGroup ───────────────────────────────────────────────────────────

interface RichRadioGroupProps extends React.ComponentProps<typeof RadioGroupPrimitive.Root> {}
function RichRadioGroup(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex flex-col gap-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <RadioGroupPrimitive.Root data-slot="rich-radio-group" orientation="vertical" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
interface RichRadioGroupItemProps extends React.ComponentProps<typeof RadioGroupPrimitive.Item> {
  line1: string;
  line2?: string;
  flipped?: boolean;
  icon?: React.ReactNode;
}
function RichRadioGroupItem(t0) {
  const $ = _c(27);
  let className;
  let icon;
  let line1;
  let line2;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      line1,
      line2,
      flipped: t1,
      icon,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = icon;
    $[3] = line1;
    $[4] = line2;
    $[5] = props;
    $[6] = t1;
  } else {
    className = $[1];
    icon = $[2];
    line1 = $[3];
    line2 = $[4];
    props = $[5];
    t1 = $[6];
  }
  const flipped = t1 === undefined ? false : t1;
  const t2 = flipped ? "flex-row-reverse" : "flex-row";
  let t3;
  if ($[7] !== className || $[8] !== t2) {
    t3 = cn("group flex w-60 cursor-pointer items-start text-left", "rounded-md border border-border bg-card", "gap-3 p-3", t2, "outline-none transition-colors", "data-[state=checked]:bg-muted", "focus-visible:ring-[3px] focus-visible:ring-ring/50", "disabled:pointer-events-none disabled:opacity-50", className);
    $[7] = className;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== icon) {
    t4 = icon ? <span aria-hidden="true" className="flex size-5 shrink-0 items-center justify-center text-muted-foreground">{icon}</span> : <div className="flex size-4 shrink-0 items-center justify-center rounded-full border border-input"><RadioGroupPrimitive.Indicator className="size-2 rounded-full bg-foreground" /></div>;
    $[10] = icon;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = flipped && "flex-1";
  let t6;
  if ($[12] !== t5) {
    t6 = cn("flex flex-col gap-0.5", t5);
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== line1) {
    t7 = <span className="heading-sm text-foreground">{line1}</span>;
    $[14] = line1;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== line2) {
    t8 = line2 && <span className="body-sm text-muted-foreground">{line2}</span>;
    $[16] = line2;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t6 || $[19] !== t7 || $[20] !== t8) {
    t9 = <div className={t6}>{t7}{t8}</div>;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== props || $[23] !== t3 || $[24] !== t4 || $[25] !== t9) {
    t10 = <RadioGroupPrimitive.Item data-slot="rich-radio-group-item" className={t3} {...props}>{t4}{t9}</RadioGroupPrimitive.Item>;
    $[22] = props;
    $[23] = t3;
    $[24] = t4;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
}
export { RadioGroup, RadioGroupItem, RichRadioGroup, RichRadioGroupItem, type RadioGroupProps, type RadioGroupItemProps, type RichRadioGroupProps, type RichRadioGroupItemProps };